import { percent } from "@amcharts/amcharts5";
import { IPieChartSettings, PieChart, PieSeries } from "@amcharts/amcharts5/percent";
import { XYChart } from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, map, pick } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

type DoughnutDataItem = {
  [key: string]: string | number | { categoryField: string; valueField: string };
};

const EXTRA_LABEL_PADDING_DEFAULT = 80;
const EXTRA_LABEL_PADDING_EXTRA = 80;

export default class extends BaseChart {
  valueField!: string;
  categoryField!: string;
  private dataLabelsTotal = 0;

  initChart() {
    this.root.container.children.clear();
    this.createChart();

    this.createSeries(this.settings.displayMetric);
    this.showLegend(
      map(this.data, this.settings.decompositionMetric) as string[],
      (this.chart as XYChart).series.values[0].dataItems as LegendData,
      false
    );

    this.categoryField = this.settings.decompositionMetric;
    this.valueField = this.settings.displayMetric;
  }

  protected createChart() {
    const holeSize = Number(this.settings.holeSize);
    const innerRadius = Number.isNaN(holeSize) ? this.settings.holeSize.default : holeSize;

    const scale = Number(this.settings.scale);
    const outerRadius = Number.isNaN(scale) ? 100 : scale;

    const chartSettings: IPieChartSettings = {
      ...chartInitialSettings,
      radius: percent(outerRadius),
      y: percent(0),
      layout: this.layout,
    };

    if (this.settings?.showDataLabels) {
      const extraAir = this.settings.dataLabelsShowPercent ? EXTRA_LABEL_PADDING_EXTRA : EXTRA_LABEL_PADDING_DEFAULT;

      if (this.settings.legendPosition === "right") {
        chartSettings.paddingLeft = 40;
        chartSettings.paddingRight = 0;
      } else if (this.settings.legendPosition === "left") {
        chartSettings.paddingLeft = 0;
        chartSettings.paddingRight = 40;
      } else {
        chartSettings.paddingLeft = extraAir;
        chartSettings.paddingRight = extraAir;
      }
    }

    if (innerRadius !== 0) {
      chartSettings.innerRadius = percent((innerRadius * outerRadius) / 100);
    }

    this.chart = this.root.container.children.push(PieChart.new(this.root, chartSettings));
  }

  protected createSeries(seriesName: string) {
    const series = (this.chart as PieChart).series.push(
      PieSeries.new(this.root, {
        name: seriesName,
        valueField: this.valueField,
        categoryField: this.categoryField,
      })
    );

    // Turn off shifting on click.
    series.slices.template.set("toggleKey", "none");
    // Turn off scaling on hover.
    series.slices.template.states.create("hover", { scale: 1 });

    this.createTooltip(series as TooltipSeries, (target) => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext;

      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          param.push({ key, value });
        });
      }
      return param;
    });

    series.get("colors")?.set("colors", this.palette);

    const metricFormatter = this.formatters?.[this.valueField];
    const formatterConfig = this.getDataLabelsFormatterConfig();
    const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

    if (this.settings.showDataLabels) {
      series.set("alignLabels", true);
      series.labels.template.setAll({
        visible: true,
        forceHidden: false,
        populateText: false,
        fontSize: labelFontSize,
      });
      series.labels.template.adapters.add("text", (text, target) => {
        const context = target.dataItem?.dataContext as Record<string, unknown> | undefined;
        const value = context?.[this.valueField];
        if (typeof value !== "number") {
          return "";
        }

        const baseText = metricFormatter
          ? metricFormatter(value, formatterConfig)
          : this.formatRawValue(value, formatterConfig);
        const percentText = this.getPercentSuffix(value);
        const normalizedBaseText = `${baseText}`;

        if (!percentText) {
          return normalizedBaseText;
        }

        return `${normalizedBaseText} (${percentText})`;
      });

      series.ticks.template.setAll({
        visible: true,
        forceHidden: false,
        // TODO: use if lines styling is requested
        // strokeWidth: 1,
        // strokeOpacity: 0.5,
      });
    } else {
      series.labels.template.setAll({
        visible: false,
        forceHidden: true,
        fontSize: labelFontSize,
      });
      series.ticks.template.setAll({
        visible: false,
        forceHidden: true,
        // strokeWidth: 2,
        // strokeOpacity: 0.4,
      });
    }

    series.data.setAll(this.data);
  }

  protected mapData(data: ChartDataRecord[]) {
    this.dataLabelsTotal = 0;

    if (!data || !Object.keys(data)) {
      return [];
    }
    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      this.module === PLAN_MODULE &&
      !this.settings.useExploreData &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const decompositionMetric = this.settings.decompositionMetric;
      const displayMetric = this.settings.displayMetric;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    this.categoryField = this.settings.decompositionMetric;
    this.valueField = this.settings.displayMetric;
    preparedData = preparedData.map((item) => pick(item, [this.categoryField, this.valueField]));

    if (!this.categoryField || !this.valueField) {
      return [];
    }

    let groupedData: Record<string, number> = {};

    switch (this.categoryField) {
      default:
        groupedData = preparedData.reduce((acc: Record<string, number>, curr) => {
          return {
            ...acc,
            [(curr[this.categoryField] || "null") as string]:
              ((acc[this.categoryField] || 0) as number) + (curr[this.valueField] as number) || 0,
          };
        }, {});
        break;
    }
    let resultData: DoughnutDataItem[] = Object.keys(groupedData)
      .map((key: string) => ({
        [this.categoryField]: key,
        [this.valueField]: groupedData[key],
        tooltipValues: {
          categoryField: this.formatters[this.categoryField](key),
          valueField: this.formatters[this.valueField](groupedData[key]),
        },
      }))
      .sort((a, b) => (b[this.valueField] as number) - (a[this.valueField] as number));

    resultData = this.showItems(resultData, this.categoryField, this.settings.displayMetric);

    this.dataLabelsTotal = resultData.reduce((acc, item) => {
      const raw = item[this.valueField];
      const numeric = typeof raw === "number" ? raw : Number(raw);
      return acc + (Number.isFinite(numeric) ? numeric : 0);
    }, 0);

    return resultData;
  }

  private formatRawValue(value: number, formatterConfig: Record<string, unknown>): string {
    return this.root.numberFormatter.format(value, formatterConfig);
  }

  private shouldShowPercentLabels(): boolean {
    return Boolean(this.settings?.dataLabelsShowPercent);
  }

  private getPercentSuffix(value: number): string | undefined {
    if (!this.shouldShowPercentLabels() || !Number.isFinite(value) || this.dataLabelsTotal <= 0) {
      return undefined;
    }

    const share = value / this.dataLabelsTotal;
    if (!Number.isFinite(share)) {
      return undefined;
    }

    const decimals = this.getDataLabelsDecimals();
    const precision = decimals !== undefined ? decimals : 1;
    const clampedPrecision = Math.min(Math.max(precision, 0), 6);
    const percentValue = share * 100;

    return `${percentValue.toFixed(clampedPrecision)}%`;
  }
}
